import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';

// 定义捐赠活动类型
interface DonationEvent {
    id: string;
    title: string;
    titleEn?: string;
    order: number;
    active: boolean;
}

// 用于生成颜色的辅助函数
const generateEventColors = (index: number) => {
    // 预定义的颜色组合数组（背景色和文字色）
    const colorPairs = [
        { bg: 'bg-blue-50', text: 'text-blue-800' },
        { bg: 'bg-green-50', text: 'text-green-800' },
        { bg: 'bg-yellow-50', text: 'text-yellow-800' },
        { bg: 'bg-red-50', text: 'text-red-800' },
        { bg: 'bg-purple-50', text: 'text-purple-800' },
        { bg: 'bg-indigo-50', text: 'text-indigo-800' },
        { bg: 'bg-pink-50', text: 'text-pink-800' },
        { bg: 'bg-teal-50', text: 'text-teal-800' },
    ];
    
    // 使用索引取模获取颜色，确保循环使用
    return colorPairs[index % colorPairs.length];
};

const DonateEvents: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [events, setEvents] = useState<DonationEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const isEnglish = i18n.language === 'en';

    // 获取捐赠活动数据
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // 创建查询，获取所有活动的捐赠活动
                const eventsQuery = query(
                    collection(db, 'donateEvents'),
                    where('active', '==', true),
                    orderBy('order', 'asc')
                );
                
                const querySnapshot = await getDocs(eventsQuery);
                
                // 处理查询结果
                const eventsData: DonationEvent[] = [];
                querySnapshot.forEach(doc => {
                    const data = doc.data() as Omit<DonationEvent, 'id'>;
                    eventsData.push({
                        id: doc.id,
                        ...data
                    });
                });
                
                setEvents(eventsData);
            } catch (err) {
                console.error('获取捐赠活动数据失败:', err);
                setError(isEnglish ? 
                    'Failed to fetch fundraising events. Please try again later.' : 
                    '获取筹款活动数据失败，请稍后再试。'
                );
            } finally {
                setLoading(false);
            }
        };
        
        fetchEvents();
    }, [isEnglish]);

    // 获取活动标题（根据当前语言）
    const getEventTitle = (event: DonationEvent) => {
        return isEnglish && event.titleEn ? event.titleEn : event.title;
    };

    return (
        <motion.section 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
        >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                {t('donate.events.title', isEnglish ? 'Fundraising Events' : '筹款活动')}
            </h2>
            
            <div className="mb-6">
                <p className="text-lg text-gray-700 mb-4">
                    {t('donate.events.description', isEnglish ? 
                        'We welcome community partners to participate in our fundraising efforts. Events include:' : 
                        '我们欢迎社区伙伴参与我们的筹款活动。活动包括：'
                    )}
                </p>
                
                {/* 加载状态 */}
                {loading && (
                    <div className="py-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                        <p className="mt-4 text-gray-600">{t('common.loading', '加载中...')}</p>
                    </div>
                )}
                
                {/* 错误状态 */}
                {error && (
                    <div className="text-center py-8 bg-red-50 rounded-lg">
                        <p className="text-red-600">
                            {error}
                        </p>
                        <button
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            onClick={() => window.location.reload()}
                        >
                            {t('common.retry', '重试')}
                        </button>
                    </div>
                )}
                
                {/* 没有数据状态 */}
                {!loading && !error && events.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">
                            {t('donate.events.noData', isEnglish ? 
                                'No fundraising events available at the moment.' : 
                                '目前没有筹款活动。'
                            )}
                        </p>
                    </div>
                )}
                
                {/* 事件列表 */}
                {!loading && !error && events.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                        {events.map((event, index) => {
                            const { bg, text } = generateEventColors(index);
                            return (
                                <div key={event.id} className={`${bg} p-4 rounded-lg`}>
                                    <h3 className={`font-semibold ${text} mb-2`}>
                                        {getEventTitle(event)}
                                    </h3>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </motion.section>
    );
};

export default DonateEvents; 